import { IProduct, useField } from '@kizi/comparator-core';
import { Div, Label, List, ListItem, P, Span } from '@kizi/core-components';
import { formatPrice } from '../../../../../utils/global';
import { withFormProps } from '../../../../HOCs';

import React from 'react';

export interface Props {
  data: IProduct;
}

const LabelWithTooltip = withFormProps(Label);

const OptionInfo = (props: {
  title: string;
  rows: { label: string; value: string | number }[];
}) => {
  const { title, rows } = props;
  return (
    <Div
      borderTop="1px solid"
      borderTopColor={'second.1'}
      pt={3}
      mb={2}
      data-testid="cost-option-section"
    >
      <P mb={2} fontWeight={1} fontSize={3}>
        {title}
      </P>
      <List>
        {rows.map(({ label, value }, index) => (
          <ListItem
            key={`${index}-${label}`}
            display="flex"
            justifyContent="space-between"
            mb={1}
          >
            <Div>{label}</Div>
            <Div>{value}</Div>
          </ListItem>
        ))}
      </List>
    </Div>
  );
};

const DiscountInfo = (props: {
  discounts: { label: string; value: string[] }[];
}) => {
  const { discounts } = props;
  return (
    <Div
      display="flex"
      flexDirection="column"
      data-testid="cost-discounts-section"
    >
      <P fontWeight={1} fontSize={3}>
        Kortingen
      </P>
      {discounts.map((item, index) => (
        <Div
          key={item.label}
          display="flex"
          flexDirection="column"
          mb={2}
          pt={3}
          borderTop={index === 0 ? '' : '1px solid'}
          borderTopColor={'second.1'}
        >
          <P fontSize={3}>{item.label}</P>
          {item.value.map((el) => (
            <P
              key={el}
              color="second.0"
              dangerouslySetInnerHTML={{ __html: el }}
            />
          ))}
        </Div>
      ))}
    </Div>
  );
};

const ContractInfo = (props: {
  data: { label: string; value: string; popover: string }[];
}) => {
  const { data } = props;
  return (
    <Div data-testid="cost-contract-section">
      <P fontWeight={1} fontSize={4} color="main.3" my={3}>
        Contractinformatie
      </P>
      {data.map((item, index) => (
        <P
          display="flex"
          justifyContent="space-between"
          key={item.label}
          mb={2}
          pt={3}
          borderTop={index === 0 ? '' : '1px solid'}
          borderColor={index === 0 ? '' : 'second.1'}
        >
          <Span>{item.label}</Span>
          <LabelWithTooltip
            _containerProps={{
              variant: 'contractLabel',
            }}
            _label={item.value}
            _tooltip={item.popover}
          />
        </P>
      ))}
    </Div>
  );
};

export const CostTab: React.FC<Props> = (props) => {
  const { data } = props;
  const filters = useField('filters');

  const extraPackages = JSON.parse(filters?.value as string).channelsPackages;
  const energySupplier = JSON.parse(filters?.value as string).energySupplier;
  const mobileProvider = JSON.parse(filters?.value as string).mobileProvider;

  const getDateLabel = (value: number, period: string) => {
    if (period === 'jaar' || value === 1) {
      return `${value} ${period}`;
    }

    return `${value} ${period}en`;
  };

  const connectionPrice = data.priceInfo.connection || 0;
  const postagePrice = data.priceInfo.postage || 0;

  const packagesToShow = data.packages?.filter((item) =>
    extraPackages.includes(item.name),
  );
  const packagesToShowCost =
    packagesToShow?.reduce((accumulator, item) => {
      return accumulator + Number(item.costs.monthlyCosts.costSpecifics.rate);
    }, 0) || 0;

  const discount = data.priceInfo.discountIncluded;
  const cbDiscount = data.priceInfo.providerDiscount || 0;

  const cashbackInfo = {
    title: discount > cbDiscount ? 'Cashback' : 'Cashback Consumentenbond',
    value: data.priceInfo.discountIncluded,
  };

  const discountInfo = {
    title: `Korting eerste ${Math.min(data.priceInfo.priceList[0]?.periodInMonth || 0, 12)} maanden`,
    value:
      (data.priceInfo.total - data.priceInfo.priceList[0]?.amount) *
      Math.min(data.priceInfo.priceList[0]?.periodInMonth || 0, 12),
  };

  const modifyPrice = (value: number | undefined, replace?: boolean) => {
    const result = !value ? Number(value) : value.toFixed(2);
    return replace && typeof result === 'string'
      ? result.replace('.', ',')
      : result;
  };

  const getMonthlyDiscount = () => {
    let text = '';

    if (data.priceInfo.priceList[0].amount !== data.priceInfo.total) {
      const amountOfMonthDiscount = data.priceInfo?.priceList[0].periodInMonth;
      const monthDiscount = data.priceInfo?.priceList[0].amount;
      text = `Eerste ${amountOfMonthDiscount} maanden voor € ${modifyPrice(
        monthDiscount,
        true,
      )}`;

      return (
        <P mb={1} color="second.0" dangerouslySetInnerHTML={{ __html: text }} />
      );
    }

    return null;
  };

  const getBenefits = () => {
    const selectedProviders = [
      ...(energySupplier?.length ? energySupplier : []),
      ...(mobileProvider?.length ? mobileProvider : []),
    ];

    const benefits =
      selectedProviders.length && data.benefits?.length
        ? data.benefits
            .filter((benefit) => selectedProviders.includes(benefit.id))
            .flatMap((benefitItem) => benefitItem.description)
        : [];
    const filteredBenefits = [...new Set(benefits)];

    if (benefits.length) {
      return [
        {
          label: 'Combikorting',
          value: filteredBenefits,
        },
      ];
    }

    return [];
  };

  const initialDiscounts = [
    {
      label: data.priceInfo.priceList[0]?.amount
        ? `De eerste ${
            data.priceInfo.priceList[0].periodInMonth
          } maanden voor ${formatPrice(
            data.priceInfo.priceList[0].amount,
          )} per maand.`
        : '',
      value:
        data.priceInfo.priceList[0]?.amount &&
        data.priceInfo.priceList[1]?.amount
          ? [`Daarna betaal je ${formatPrice(data.priceInfo.total)} per maand.`]
          : [''],
    },
  ];
  if (cbDiscount && cbDiscount > 0) {
    initialDiscounts.push({
      label: `${formatPrice(
        data.priceInfo.discountSource === '-1' ? cbDiscount : discount,
      )} eenmalige korting via ons.`,
      value: [
        data.priceInfo.discountSource === 'provider'
          ? `De cashback wordt door ${data.provider.name} verrekend op de factuur. De cashback is al meegenomen in de prijs.`
          : 'De cashback moet binnen 4 weken na aansluiting aangevraagd worden en wordt vervolgens binnen 8 tot 10 weken uitbetaald.',
      ],
    });
  }
  const discounts = [...initialDiscounts, ...getBenefits()];

  const installationCost = data.priceInfo?.installation?.filter(
    (cost) => cost.required,
  );

  const installationCostRows = installationCost.map(({ name, rate }) => {
    return {
      label: name,
      value: formatPrice(rate),
    };
  });

  const totalinstallationCost = installationCost.reduce((acc, curr) => {
    return acc + curr.rate;
  }, 0);

  return (
    <>
      <Div
        display="flex"
        flexDirection={['column', 'row', 'row']}
        width={'100%'}
      >
        <Div width={['100%', '50%', '50%']} pr={[0, 0, 7]} pt={[2, 0, 0]}>
          <P fontWeight={1} fontSize={4} color="main.3" mb={2}>
            Maandelijkse kosten
          </P>
          <P display="flex" justifyContent="space-between" mb={1}>
            <Span>{data.provider.name}</Span>
            <Span>{formatPrice(data.priceInfo.total)}</Span>
          </P>
          {getMonthlyDiscount()}
          {data.packages?.map((item) => {
            if (extraPackages.includes(item.name)) {
              return (
                <P
                  display="flex"
                  justifyContent="space-between"
                  pl={5}
                  mb={1}
                  key={item.name}
                  data-testid="selected-package"
                >
                  <Span>+ {item.name}</Span>
                  <Span>
                    {formatPrice(item.costs.monthlyCosts.costSpecifics.rate)}
                  </Span>
                </P>
              );
            }
            return null;
          })}
          <P display="flex" justifyContent="space-between" mb={1}>
            <Span>Totaal per maand</Span>
            <Span>
              {formatPrice(data.priceInfo.total + packagesToShowCost)}
            </Span>
          </P>

          <OptionInfo
            title="Eenmalige kosten"
            rows={[
              ...installationCostRows,
              {
                label: 'Eenmalige aansluitkosten',
                value: formatPrice(connectionPrice),
              },
              {
                label: 'Verzend- en administratiekosten',
                value: formatPrice(postagePrice),
              },
              {
                label: 'Totale eenmalige kosten',
                value: formatPrice(
                  connectionPrice + postagePrice + totalinstallationCost,
                ),
              },
            ]}
          />
          <OptionInfo
            title="Kortingen"
            rows={[
              {
                label: discountInfo.title,
                value: `- ${formatPrice(discountInfo.value)}`,
              },
              {
                label: cashbackInfo.title,
                value: formatPrice(-cashbackInfo.value),
              },
              {
                label: 'Totale korting',
                value: `- ${formatPrice(
                  discountInfo.value + cashbackInfo.value,
                )}`,
              },
            ]}
          />
          <OptionInfo
            title="Totaal"
            rows={[
              {
                label: 'Totale jaarlijkse kosten',
                value: formatPrice(
                  (data.priceInfo.total + packagesToShowCost) * 12,
                ),
              },
              {
                label: 'Totale eenmalige kosten',
                value: formatPrice(
                  connectionPrice + postagePrice + totalinstallationCost,
                ),
              },
              {
                label: 'Totale Korting',
                value: formatPrice(-(discountInfo.value + cashbackInfo.value)),
              },
            ]}
          />
          <P
            fontWeight={1}
            fontSize={4}
            display="flex"
            justifyContent="space-between"
            borderTop="3px solid"
            borderTopColor="main.3"
            paddingTop={2}
          >
            <Span>Totale kosten eerste jaar</Span>
            <Span>
              {formatPrice(
                (data.priceInfo.total + packagesToShowCost) * 12 -
                  discountInfo.value -
                  cashbackInfo.value +
                  connectionPrice +
                  postagePrice +
                  totalinstallationCost,
              )}
            </Span>
          </P>
        </Div>
        <Div width={['100%', '50%', '50%']} pr={[0, 0, 7]}>
          <DiscountInfo discounts={discounts} />
          <ContractInfo
            data={[
              {
                label: 'Gemiddelde levertijd',
                value: getDateLabel(
                  Number(data.contractInformation?.deliveryTime.text),
                  String(data.contractInformation?.deliveryTime.unit),
                ),
                popover:
                  'De gemiddelde levertijd vanaf de aanvraag tot aan actieve levering door de aanbieder. Let op: Wanneer je al een abonnement hebt op dit adres, en je gebruikt maakt van de overstapservice, dan is de gemiddelde levertijd 30 dagen. Zo voorkom je dat je dubbel betaalt.',
              },
              {
                label: 'Vrije ingangsdatum',
                value: getDateLabel(
                  Number(data.contractInformation?.freeStartDate.period.text),
                  String(data.contractInformation?.freeStartDate.period.unit),
                ),
                popover:
                  'De uiterste datum waarop je je nieuwe contract in kunt laten gaan.',
              },
              {
                label: 'Contractduur',
                value: getDateLabel(
                  Number(data.contractInformation?.contractDuration.text),
                  String(data.contractInformation?.contractDuration.unit),
                ),
                popover: 'De minimale contractduur van dit contract.',
              },
              {
                label: 'Contractverlenging',
                value: getDateLabel(
                  Number(data.contractInformation?.contractExtension.text),
                  String(data.contractInformation?.contractExtension.unit),
                ),
                popover:
                  'De termijn waarbij dit contract wordt verlengd na de minimale contractduur.',
              },
              {
                label: 'Opzegtermijn',
                value: getDateLabel(
                  Number(data.contractInformation?.noticePeriod.text),
                  String(data.contractInformation?.noticePeriod.unit),
                ),
                popover:
                  'De opzegtermijn voor het einde van de minimale contractduur.',
              },
            ]}
          />
        </Div>
      </Div>
      <P color="second.0" mt={6}>
        {'Let op: De provider kan de tarieven jaarlijks indexeren.'}
      </P>
    </>
  );
};

const parsePrice = (price?: string): number =>
  parseFloat(price?.replace(',', '.') || '0');

export const OsCostTab = ({ data, onlyShowCostDetails = false }) => {
  const additionalChannels = useField('additionalChannels');
  const additionalChannelsValue = JSON.parse(
    additionalChannels?.value as string,
  );
  const mobileProvider = useField('mobileProvider');
  const mobileProviderValue = JSON.parse(mobileProvider?.value as string);
  const energySupplier = useField('energySupplier');
  const energySupplierValue = JSON.parse(energySupplier?.value as string);

  const showZiggoSport =
    additionalChannelsValue.includes('ziggoSport') &&
    data.ziggoSport === 'true';
  const showEspn =
    additionalChannelsValue.includes('espn') && data.espn === 'true';
  const ziggoSportPrice = showZiggoSport ? parsePrice(data.ziggoSportPrice) : 0;
  const espnPrice = showEspn ? parsePrice(data.espnPrice) : 0;
  const totalPerMonth = data.priceInfo?.total + ziggoSportPrice + espnPrice;

  const totalYearlyCost = totalPerMonth * 12;

  let discountMonths = 0;
  if (data.durationForDiscount) {
    if (Number(data.durationForDiscount) > 12) {
      // If durationForDiscount is above 12, we should calculate with 12
      discountMonths = 12;
    } else {
      // If it's 12 or lower, we should calculate with the value which comes from durationForDiscount
      discountMonths = Number(data.durationForDiscount);
    }
  }

  const discountFirstMonths =
    data.durationForDiscount && data.priceMonthWithDiscount
      ? (data.priceInfo.total - parsePrice(data.priceMonthWithDiscount)) *
        discountMonths
      : 0;

  const onceOffCosts = parsePrice(data.onceOffCosts);
  const discountThroughUs = parsePrice(data.discountThroughUs);
  const discountProvider = parsePrice(data.discountProvider);
  // const combinationDiscount = parsePrice(data.combinationDiscount);
  const totalFirstYearCost =
    totalYearlyCost -
    discountFirstMonths +
    onceOffCosts -
    discountThroughUs -
    discountProvider;

  const combinationDiscountMapping = {
    'Ben': 'krn:kizi:product-supplier:ed616322',
    'Budget-Mobiel': 'krn:kizi:product-supplier:7ac52024',
    'Caiway': 'krn:kizi:product-supplier:a8d62220',
    'Delta': 'krn:kizi:product-supplier:481136c4',
    'hollandsnieuwe': 'krn:kizi:product-supplier:603f4baf',
    'KPN': 'krn:kizi:product-supplier:f318d224',
    'Odido': 'krn:kizi:product-supplier:ed616322',
    'Simyo': 'krn:kizi:product-supplier:f318d224',
    'Vodafone': 'krn:kizi:product-supplier:603f4baf',
    'Budget Energie': 'krn:kizi:product-supplier:7ac52024',
  };

  // Display text for discount period
  const displayDiscountMonths = data.durationForDiscount
    ? Number(data.durationForDiscount) > 12
      ? '12'
      : data.durationForDiscount
    : '';
  return (
    <>
      <Div
        display="flex"
        flexDirection={['column', 'column', 'row']}
        width="100%"
        gridRowGap={6}
      >
        <Div
          width={['100%', '100%', onlyShowCostDetails ? '100%' : '50%']}
          pr={[0, 0, onlyShowCostDetails ? 0 : 7]}
          pt={[2, 0, 0]}
        >
          <P variant="bodyImportant" fontSize="20px!important" mb={2}>
            {'Prijsopbouw'}
          </P>
          <Div display="flex" justifyContent="space-between" mb={1}>
            <P variant="footnoteRegular">{data.subscriptionName}</P>
            <P variant="footnoteRegular">
              {formatPrice(data.priceInfo?.total)}
            </P>
          </Div>
          {showZiggoSport && (
            <Div display="flex" justifyContent="space-between" mb={1} ml={4}>
              <P variant="footnoteRegular" color="main.5">
                {'+ Ziggo Sport'}
              </P>
              <P variant="footnoteRegular" color="main.5">
                {formatPrice(ziggoSportPrice)}
              </P>
            </Div>
          )}
          {showEspn && (
            <Div display="flex" justifyContent="space-between" mb={1} ml={4}>
              <P variant="footnoteRegular" color="main.5">
                {'+ ESPN'}
              </P>
              <P variant="footnoteRegular" color="main.5">
                {formatPrice(espnPrice)}
              </P>
            </Div>
          )}
          <Div display="flex" justifyContent="space-between" mb={1}>
            <P variant="footnoteRegular">{'Totaal per maand'}</P>
            <P variant="footnoteRegular">{formatPrice(totalPerMonth)}</P>
          </Div>
          <P
            variant="footnoteRegular"
            textAlign="end"
            borderBottom="2px solid"
            pb={2}
          >
            {'x12'}
          </P>
          <Div display="flex" justifyContent="space-between" my={1}>
            <P variant="footnoteImportant">{'Totaal jaarlijkse kosten'}</P>
            <P variant="footnoteImportant">{formatPrice(totalYearlyCost)}</P>
          </Div>
          {data.durationForDiscount && data.priceMonthWithDiscount && (
            <Div
              display="flex"
              justifyContent="space-between"
              my={1}
              borderBottom="2px solid"
              pb={2}
            >
              <P variant="footnoteRegular">
                {`Korting eerste ${displayDiscountMonths} maanden`}
              </P>
              <P variant="footnoteRegular">
                {`- ${formatPrice(discountFirstMonths)}`}
              </P>
            </Div>
          )}
          <Div display="flex" justifyContent="space-between" my={1}>
            <P variant="footnoteRegular">{'Eenmalige kosten'}</P>
            <P variant="footnoteRegular">{formatPrice(onceOffCosts)}</P>
          </Div>
          {discountProvider > 0 && (
            <Div display="flex" justifyContent="space-between" my={1}>
              <P variant="footnoteRegular">{'Korting via provider'}</P>
              <P variant="footnoteRegular">{`- ${formatPrice(discountProvider)}`}</P>
            </Div>
          )}
          {discountThroughUs > 0 && (
            <Div display="flex" justifyContent="space-between" my={1}>
              <P variant="footnoteRegular">{'Korting via ons'}</P>
              <P variant="footnoteRegular">{`- ${formatPrice(discountThroughUs)}`}</P>
            </Div>
          )}
          <Div
            fontWeight={1}
            fontSize={4}
            display="flex"
            justifyContent="space-between"
          >
            <P variant="bodyImportant">{'Totale kosten eerste jaar'}</P>
            <P variant="bodyImportant">{formatPrice(totalFirstYearCost)}</P>
          </Div>
        </Div>
        {onlyShowCostDetails ? null : (
          <Div width={['100%', '100%', '50%']}>
            <P variant="bodyImportant" fontSize="20px!important" mb={2}>
              {'Kortingen'}
            </P>
            {data.priceMonthWithDiscount && (
              <Div
                display="flex"
                justifyContent="space-between"
                mb={3}
                borderBottom={
                  data.combinationDiscount || discountThroughUs > 0
                    ? '1px solid'
                    : null
                }
                borderBottomColor="second.1"
                pb={4}
              >
                <P variant="footnoteRegular">
                  {`De eerste ${displayDiscountMonths} maanden voor ${formatPrice(parsePrice(data.priceMonthWithDiscount))} per maand. `}
                  <Span variant="footnoteRegular" color="main.5">
                    {`Daarna betaal je ${formatPrice(data.priceInfo.total)} per maand.`}
                  </Span>
                </P>
                {/* <P variant="footnoteRegular" minWidth="fit-content">
                  {`- ${formatPrice(discountFirstMonths)}`}
                </P> */}
              </Div>
            )}
            {discountThroughUs > 0 && (
              <Div
                display="flex"
                justifyContent="space-between"
                mb={3}
                borderBottom={data.combinationDiscount ? '1px solid' : null}
                borderBottomColor="second.1"
                pb={4}
              >
                <Div>
                  <P variant="footnoteRegular">
                    {`${formatPrice(discountThroughUs)} eenmalige korting via ons.`}
                  </P>
                  <P variant="footnoteRegular" color="main.5">
                    {
                      'De korting wordt binnen 3 maanden na start van het abonnement uitgekeerd op het bij de aanvraag opgegeven rekeningnummer.'
                    }
                  </P>
                </Div>
                <P variant="footnoteRegular" minWidth="fit-content">
                  {`- ${formatPrice(discountThroughUs)}`}
                </P>
              </Div>
            )}
            {
              // combinationDiscount > 0 &&
              [...mobileProviderValue, ...energySupplierValue].some(
                (el) => combinationDiscountMapping[el] === data.provider.krn,
              ) && (
                <Div display="flex" justifyContent="space-between" mb={1}>
                  <Div>
                    <P variant="footnoteRegular">{'Combinatie korting'}</P>
                    <P variant="footnoteRegular" color="main.5">
                      {data.combinationDiscountInformation}
                    </P>
                  </Div>
                  {/* <P variant="footnoteRegular" minWidth="fit-content">
                    {`- ${formatPrice(combinationDiscount)}`}
                  </P> */}
                </Div>
              )
            }
          </Div>
        )}
      </Div>
      {onlyShowCostDetails ? null : (
        <P variant="captionRegular" color="main.5" mt={2}>
          {'Let op: De provider kan de tarieven jaarlijks indexeren'}
        </P>
      )}
    </>
  );
};
